import React, { useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useGetProductQuery as useGetProductQueryApi } from '../../../redux/apis/endpoints/productApi';
import { useGetFulfillmentGroupDetailsQuery } from '../../../redux/apis/endpoints/orderApi';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import MultiItemReviewForm from './MultiItemReviewForm';
import SingleItemReviewForm from './SingleItemReviewForm';

const ProductReviewPage = () => {
  const { productId: paramProductId, fulfillmentGroupId: fulfillmentGroupIdFromParams } = useParams();
  const history = useHistory();
  const location = useLocation();
  
  // Fetch product data for single product reviews
  const { data: product, isLoading: isLoadingProduct, error: productError } = 
    useGetProductQueryApi(paramProductId, { skip: !paramProductId });

  // Fetch fulfillment group when multi-review page
  const { data: fgData, isLoading: isLoadingFG } = useGetFulfillmentGroupDetailsQuery(fulfillmentGroupIdFromParams, {
    skip: !fulfillmentGroupIdFromParams
  });

  // Debug logging
  useEffect(() => {
    console.log('ProductReviewPage mounted with params:', { paramProductId, fulfillmentGroupIdFromParams });
    console.log('Location state:', location.state);
    
    if (location.state || fulfillmentGroupIdFromParams) {
      console.log('Key relationship IDs:', {
        orderId: location.state?.orderId,
        merchantId: location.state?.merchantId,
        fulfillmentGroupId: fulfillmentGroupIdFromParams || location.state?.fulfillmentGroupId
      });
      
      // If we have a fulfillmentGroupId but no orderItemData, try to fetch the fulfillment group
      const currentFulfillmentGroupId = fulfillmentGroupIdFromParams || location.state?.fulfillmentGroupId;
      if (currentFulfillmentGroupId && !location.state?.orderItemData && paramProductId) {
        console.log('Fetching fulfillment group to get order item data');
        
        fetch(`${process.env.REACT_APP_BACKEND_URL}/fulfillment-groups/${currentFulfillmentGroupId}`)
          .then(response => response.json())
          .then(data => {
            if (data && data.orderItems && Array.isArray(data.orderItems)) {
              const matchingOrderItem = data.orderItems.find(
                item => item.product && item.product.id === parseInt(paramProductId, 10)
              );
              
              if (matchingOrderItem) {
                console.log('Found matching order item in fulfillment group:', matchingOrderItem);
                location.state = {
                  ...location.state,
                  orderItemData: matchingOrderItem
                };
              }
            }
          })
          .catch(error => console.error('Error fetching fulfillment group:', error));
      }
    }
  }, [paramProductId, fulfillmentGroupIdFromParams, location.state]);

  // Loading states
  if ((paramProductId && isLoadingProduct) || isLoadingFG) {
    return <div className="max-w-4xl mx-auto p-4"><LoadingSpinner /></div>;
  }

  // Error states
  if (productError) {
    return <div className="text-red-500 text-center p-4">Error loading product: {productError.toString()}</div>;
  }

  // Determine which form to render
  const shouldRenderMultiForm = fulfillmentGroupIdFromParams && fgData && fgData.orderItems && fgData.orderItems.length > 0;
  
  if (shouldRenderMultiForm) {
    // Redirect to multi-item form if we have a productId param but should be multi-form
    if (paramProductId) {
      history.replace(`/fulfillmentgroup/${fulfillmentGroupIdFromParams}/review-products`);
      return null;
    }
    
    return <MultiItemReviewForm fulfillmentGroupId={fulfillmentGroupIdFromParams} />;
  }

  // Single product review form
  const activeProduct = paramProductId ? product : (fgData?.orderItems?.[0]?.product || null);
  
  return (
    <SingleItemReviewForm 
      product={activeProduct}
      paramProductId={paramProductId}
      fulfillmentGroupId={fulfillmentGroupIdFromParams}
    />
  );
};

export default ProductReviewPage; 